// TelcoTrack — VYKOP podtyp trasy (MET / UR / R100) pro import, koridor a břemena.
// Field routes: dokop (odbočka) → R100; every other field route → UR.
// Import mapping may still store MET / UR / R100. PD project R100 (not dokop) stays R100.
//
// PD-imported R100 vs field-realized (dokop) R100:
// - PD project R100 stays in DB for Příprava / DOSS / VBŘ / corridor.
// - Realization map + excavate KPI treat only dokop/branch-draw R100 as live VYKOP.

import { normalizeObjectTypeForLookup } from './object-workflow/phase-codes';
import {
  getVykopVariantId,
  isDokopVykop,
  type VykopVariantLookup,
} from './vykop-variant.constants';

export const VYKOP_SEGMENT_KINDS = ['MET', 'UR', 'R100'] as const;
export type VykopSegmentKind = (typeof VYKOP_SEGMENT_KINDS)[number];

/** Import / unmarked hlavní trasa default. */
export const DEFAULT_VYKOP_SEGMENT_KIND: VykopSegmentKind = 'UR';

/** User-drawn BUDOVA odbočka (branch-draw / dokop) — always R100. */
export const BRANCH_DRAW_VYKOP_SEGMENT_KIND: VykopSegmentKind = 'R100';

/**
 * Custom-field keys written by GeoJSON / DXF / unified import.
 * Present → PD corridor; absent on a VYKOP → field-drawn route.
 */
export const IMPORTED_PROJECT_VYKOP_METADATA_KEYS = [
  'importedFrom',
  'originalLayer',
  'importFileName',
  'importId',
  'importBatchId',
] as const;

export const VYKOP_SEGMENT_KIND_METADATA_KEY = 'vykopSegmentKind';

export const VYKOP_SEGMENT_KIND_LABELS_CS: Record<VykopSegmentKind, string> = {
  MET: 'MET (metalika / MET)',
  UR: 'UR (území / hlavní)',
  R100: 'R100 (odbočka k budově)',
};

/** Short labels for map / table filters. */
export const VYKOP_SEGMENT_KIND_FILTER_LABELS: Record<VykopSegmentKind, string> = {
  R100: 'R100',
  MET: 'MET',
  UR: 'UR',
};

/** Filter UI order (R100, MET, UR). */
export const VYKOP_SEGMENT_KIND_FILTER_ORDER: readonly VykopSegmentKind[] = ['R100', 'MET', 'UR'];

export const VYKOP_SEGMENT_KIND_FILTER_OPTIONS: ReadonlyArray<{
  value: VykopSegmentKind;
  label: string;
}> = VYKOP_SEGMENT_KIND_FILTER_ORDER.map((id) => ({
  value: id,
  label: VYKOP_SEGMENT_KIND_FILTER_LABELS[id],
}));

/**
 * Map / layer sub-filters under VYKOP: segment kind only (R100 / MET / UR).
 * Structural Hlavní trasa / Dokop is no longer an active metric/filter.
 * R100 map checkbox = field-realized only (`isRealizedR100`); PD import uses
 * {@link MAP_FILTER_PD_R100_UNDERLAY_ID} / {@link PD_R100_UNDERLAY_LAYER_LABEL}.
 */
export const VYKOP_MAP_SUBFILTER_IDS_LIST: readonly string[] = [...VYKOP_SEGMENT_KIND_FILTER_ORDER];

/** Map-only: realized dokop R100 (live realization layer). */
export const MAP_FILTER_R100_REALIZED_LABEL = 'R100 (dokreslené)' as const;

export const VYKOP_MAP_SUBFILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  VYKOP_SEGMENT_KIND_FILTER_ORDER.map((id) => ({
    value: id,
    label: id === 'R100' ? MAP_FILTER_R100_REALIZED_LABEL : VYKOP_SEGMENT_KIND_FILTER_LABELS[id],
  }));

/** Koridor trasy (KN acquisition): hlavní síť bez R100. */
export const VYKOP_SEGMENT_KINDS_MAIN_CORRIDOR: readonly VykopSegmentKind[] = ['MET', 'UR'];

/** Délka Bm ve smlouvách břemen: jen R100 (odbočky). */
export const VYKOP_SEGMENT_KINDS_BREMENA_BM: readonly VykopSegmentKind[] = ['R100'];

export function isVykopSegmentKind(value: unknown): value is VykopSegmentKind {
  return typeof value === 'string' && (VYKOP_SEGMENT_KINDS as readonly string[]).includes(value.trim());
}

export function parseVykopSegmentKind(value: unknown): VykopSegmentKind | null {
  const raw = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return isVykopSegmentKind(raw) ? raw : null;
}

function readCustomFieldsRecord(metadata: unknown): Record<string, unknown> | null {
  if (metadata == null || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const m = metadata as Record<string, unknown>;
  const cf = m['customFields'] ?? m['custom_fields'];
  if (cf == null || typeof cf !== 'object' || Array.isArray(cf)) return null;
  return cf as Record<string, unknown>;
}

export function readVykopSegmentKindFromMetadata(metadata: unknown): VykopSegmentKind | null {
  const cf = readCustomFieldsRecord(metadata);
  if (!cf) return null;
  return parseVykopSegmentKind(cf[VYKOP_SEGMENT_KIND_METADATA_KEY]);
}

/** PD / CAD import corridor (not a field-drawn route). */
export function isImportedProjectVykop(params: VykopVariantLookup): boolean {
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  if (type !== 'VYKOP') return false;
  const cf = readCustomFieldsRecord(params.metadata);
  if (!cf) return false;
  return IMPORTED_PROJECT_VYKOP_METADATA_KEYS.some((key) => String(cf[key] ?? '').trim().length > 0);
}

/**
 * Kind persisted for a field route: dokop → R100, otherwise UR.
 * Import mapping is unchanged (see {@link resolveImportVykopSegmentKind}).
 */
export function fieldDrawnVykopSegmentKind(isDokop: boolean): VykopSegmentKind {
  return isDokop ? BRANCH_DRAW_VYKOP_SEGMENT_KIND : DEFAULT_VYKOP_SEGMENT_KIND;
}

/**
 * Resolved kind for display/filter.
 * Dokop always R100 (manual MET/UR/R100 does not win).
 * Other field routes always UR (including former MET and former field R100).
 * Imported corridors keep stored MET / UR / R100 (PD project R100 stays R100).
 */
export function effectiveVykopSegmentKind(params: VykopVariantLookup): VykopSegmentKind {
  if (isDokopVykop(params)) return BRANCH_DRAW_VYKOP_SEGMENT_KIND;
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  if (type === 'VYKOP' && !isImportedProjectVykop(params)) return DEFAULT_VYKOP_SEGMENT_KIND;
  const fromMeta = readVykopSegmentKindFromMetadata(params.metadata);
  if (fromMeta) return fromMeta;
  return DEFAULT_VYKOP_SEGMENT_KIND;
}

/**
 * Write field-route kind into `metadata.customFields`.
 * Dokop → R100, other field routes → UR. Imported PD corridors are left untouched.
 */
export function assignPersistedFieldVykopSegmentKind(
  metadata: Record<string, unknown> | null | undefined,
  type: string | null | undefined,
): void {
  const normalized = normalizeObjectTypeForLookup(String(type ?? '').trim());
  if (normalized !== 'VYKOP') return;
  if (metadata == null || typeof metadata !== 'object' || Array.isArray(metadata)) return;
  const params: VykopVariantLookup = { type: normalized, metadata };
  if (!isDokopVykop(params) && isImportedProjectVykop(params)) return;
  const kind = effectiveVykopSegmentKind(params);
  const existing = metadata['customFields'] ?? metadata['custom_fields'];
  const cf =
    existing != null && typeof existing === 'object' && !Array.isArray(existing)
      ? (existing as Record<string, unknown>)
      : {};
  cf[VYKOP_SEGMENT_KIND_METADATA_KEY] = kind;
  if (metadata['customFields'] == null && metadata['custom_fields'] != null) {
    metadata['custom_fields'] = cf;
  } else {
    metadata['customFields'] = cf;
  }
}

/**
 * Field-realized R100 (BUDOVA odbočka / dokop / branch-draw).
 * Live realization map + excavate KPI numerator.
 */
export function isRealizedR100(params: VykopVariantLookup): boolean {
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  if (type !== 'VYKOP') return false;
  return effectiveVykopSegmentKind(params) === 'R100' && isDokopVykop(params);
}

/**
 * PD-imported project R100 (not dokop). Kept for Příprava / DOSS / VBŘ / corridor;
 * hidden from live realization map unless optional underlay is on.
 */
export function isPdProjectR100(params: VykopVariantLookup): boolean {
  const type = normalizeObjectTypeForLookup(String(params.type ?? '').trim());
  if (type !== 'VYKOP') return false;
  return effectiveVykopSegmentKind(params) === 'R100' && !isDokopVykop(params);
}

/**
 * Map overlay filter id for optional PD-imported project R100 underlay
 * (`isPdProjectR100`; dashed, non-interactive). Listed under Typ trasy as
 * „R100 (import)“ next to realized „R100 (dokreslené)“.
 */
export const PD_R100_UNDERLAY_LAYER_ID = 'pd-r100' as const;
export const PD_R100_UNDERLAY_LAYER_LABEL = 'R100 (import)' as const;
export const MAP_FILTER_PD_R100_UNDERLAY_ID = `overlay:${PD_R100_UNDERLAY_LAYER_ID}` as const;

/** Map layer filter group: realized kinds + optional PD R100 import underlay. */
export const MAP_FILTER_TYP_TRASY_GROUP = 'Typ trasy' as const;

export function vykopSegmentMatchesKindFilter(
  params: VykopVariantLookup,
  allowedKinds: readonly VykopSegmentKind[],
): boolean {
  if (!allowedKinds.length) return true;
  return allowedKinds.includes(effectiveVykopSegmentKind(params));
}

/**
 * Map layer toggles: enabled set holds R100 / MET / UR.
 * VYKOP matches when its effective segment kind is enabled.
 * R100 toggle = field-realized only (`isRealizedR100`); PD project R100 is excluded
 * (optional underlay uses {@link isPdProjectR100} + overlay toggle separately).
 * Empty [enabledIds] → false (show nothing). Non-VYKOP always matches.
 */
export function matchesVykopMapSubFilters(
  params: VykopVariantLookup,
  enabledIds: ReadonlySet<string>,
): boolean {
  // Non-VYKOP: getVykopVariantId is null — always visible regardless of kind toggles.
  if (!getVykopVariantId(params)) return true;
  const kind = effectiveVykopSegmentKind(params);
  if (!enabledIds.has(kind)) return false;
  if (kind === 'R100' && isPdProjectR100(params)) return false;
  return true;
}

export function isVykopMapSubfilterId(id: string): boolean {
  return (VYKOP_MAP_SUBFILTER_IDS_LIST as readonly string[]).includes(id);
}

const LAYER_R100 = /(r100|r-?100|odbo[cč]k|p[rř]ipoj|dokop|branch)/i;
const LAYER_MET = /(met|metal|metalika)/i;
const LAYER_UR = /(^|[^a-z])ur([^a-z]|$)|u[rř]em[ií]|hlavn[ií]\s*tras/i;

/** Heuristika z názvu CAD vrstvy — lze přepsat v import UI. */
export function inferVykopSegmentKindFromLayerName(layerName: string | null | undefined): VykopSegmentKind | null {
  const name = (layerName ?? '').trim();
  if (!name) return null;
  if (LAYER_R100.test(name)) return 'R100';
  if (LAYER_MET.test(name)) return 'MET';
  if (LAYER_UR.test(name)) return 'UR';
  return null;
}

/**
 * Import mapping / layer heuristic; falls back to {@link DEFAULT_VYKOP_SEGMENT_KIND} (UR)
 * when neither mapping nor layer name yields a kind.
 */
export function resolveImportVykopSegmentKind(input: {
  layerName: string;
  mapping?: Record<string, VykopSegmentKind | string> | null;
}): VykopSegmentKind {
  const key = input.layerName.trim();
  const mapped = input.mapping?.[key];
  const fromMap = parseVykopSegmentKind(mapped);
  if (fromMap) return fromMap;
  return inferVykopSegmentKindFromLayerName(key) ?? DEFAULT_VYKOP_SEGMENT_KIND;
}

export function parseVykopSegmentKindByLayer(value: unknown): Record<string, VykopSegmentKind> | undefined {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const out: Record<string, VykopSegmentKind> = {};
  for (const [rawKey, raw] of Object.entries(value as Record<string, unknown>)) {
    const key = rawKey.trim();
    const kind = parseVykopSegmentKind(raw);
    if (key && kind) out[key] = kind;
  }
  return Object.keys(out).length ? out : undefined;
}
